import type { Theme } from "@mui/material/styles";
import type { AppCustomStyles } from "./types";
import { buildCommonStyles } from "./layout/common";
import { buildFormLayoutStyles } from "./layout/formLayout";
import { buildPageLayoutStyles } from "./layout/pageLayout";
import { buildSharedPageStyles } from "./pages/shared";
import { buildReportPageStyles } from "./pages/report";
import { buildPublicSharePageStyles } from "./pages/publicShare";
import { buildIngestPageStyles } from "./pages/ingest";
import { buildDashboardsPageStyles } from "./pages/dashboards";
import { buildDataPrepPageStyles } from "./pages/dataPrep";
import { buildWidgetsPageStyles } from "./pages/widgets";
import { buildDashboardQueriesPageStyles } from "./pages/dashboardQueries";
import { buildDocsPageStyles } from "./pages/docs";
import { buildReportChatScreenStyles } from "./pages/reportChatScreen";
import { buildDataPrepComponentStyles } from "./components/dataPrep";
import { buildGuideComponentStyles } from "./components/guide";
import { buildWidgetsComponentStyles } from "./components/widgets";
import { buildReportComponentStyles } from "./components/report";
import { buildLayoutComponentStyles } from "./components/layout";
import { buildAuthLoadingComponentStyles } from "./components/authLoading";
import { buildErrorModalComponentStyles } from "./components/errorModal";
import { buildNotificationComponentStyles } from "./components/notification";
import { buildChartTypeIconComponentStyles } from "./components/chartTypeIcon";
import { buildDataTableComponentStyles } from "./components/dataTable";
import { buildDataTableWithGroupingComponentStyles } from "./components/dataTableWithGrouping";
import { buildDashboardGridComponentStyles } from "./components/dashboardGrid";
import { buildWidgetOutputViewComponentStyles } from "./components/widgetOutputView";
import { buildDashboardCrossFilterBarComponentStyles } from "./components/dashboardCrossFilterBar";
import {
  buildDrillDownDialogComponentStyles,
  buildGenericDialogComponentStyles,
  buildOnboardingDialogComponentStyles,
  buildSlideOverPanelComponentStyles,
} from "./components/dialog";
import {
  buildAppColorFieldComponentStyles,
  buildAppFormFieldComponentStyles,
  buildAppRadioGroupComponentStyles,
  buildAppToggleFieldComponentStyles,
} from "./components/formFields";
import {
  buildChartEditorPanelComponentStyles,
  buildChartOptionPanelsComponentStyles,
} from "./components/chartEditor";
import {
  buildColumnFormatSelectComponentStyles,
  buildInteractiveTableViewComponentStyles,
} from "./components/interactiveTable";
import { buildTableColumnsEditorComponentStyles } from "./components/tableColumnsEditor";
import {
  buildDashboardDecorationViewComponentStyles,
  buildDecorationEditDialogComponentStyles,
  buildDecorationPaletteComponentStyles,
} from "./components/dashboardDecoration";
import {
  buildDashboardWidgetEditDialogComponentStyles,
  buildDesignSidebarComponentStyles,
  buildWidgetEditorComponentStyles,
  buildWidgetPaletteComponentStyles,
} from "./components/widgetEditor";
import {
  buildChatIndexPickerComponentStyles,
  buildOpenSearchDiscoverViewerComponentStyles,
  buildOpenSearchEventLogPanelComponentStyles,
  buildOpenSearchIndexPickerComponentStyles,
} from "./components/openSearch";
import { buildJsonCodeEditorComponentStyles, buildPdfViewerComponentStyles } from "./components/jsonCodeEditor";
import { buildDateRangeHeaderComponentStyles } from "./components/dateRangeHeader";
import { buildQueryEditorComponentStyles } from "./components/queryEditor";
import { buildAssignedQueriesPanelComponentStyles } from "./components/assignedQueriesPanel";
import { buildDecorationBindingComponentStyles } from "./components/decorationBinding";
import { buildSidenavStyles } from "./widgets/sidenav";
import { buildNavbarStyles } from "./widgets/navbar";
import { buildConfiguratorStyles } from "./widgets/configurator";
import { buildBreadcrumbsStyles } from "./widgets/breadcrumbs";
import { buildLayoutContainerStyles } from "./widgets/layoutContainers";
import { buildLegacyWidgetStyles } from "./widgets/legacy";

export function buildCustomStyles(theme: Theme): AppCustomStyles {
  return {
    form: buildFormLayoutStyles(theme),
    page: buildPageLayoutStyles(theme),
    common: buildCommonStyles(theme),
    pages: {
      shared: buildSharedPageStyles(theme),
      report: buildReportPageStyles(theme),
      publicShare: buildPublicSharePageStyles(theme),
      ingest: buildIngestPageStyles(theme),
      dashboards: buildDashboardsPageStyles(theme),
      dataPrep: buildDataPrepPageStyles(theme),
      widgets: buildWidgetsPageStyles(theme),
      dashboardQueries: buildDashboardQueriesPageStyles(theme),
      docs: buildDocsPageStyles(theme),
    },
    components: {
      guide: buildGuideComponentStyles(theme),
      dataPrep: buildDataPrepComponentStyles(theme),
      widgets: buildWidgetsComponentStyles(theme),
      report: buildReportComponentStyles(theme),
      reportChatScreen: buildReportChatScreenStyles(theme),
      layout: buildLayoutComponentStyles(theme),
      authLoading: buildAuthLoadingComponentStyles(theme),
      errorModal: buildErrorModalComponentStyles(theme),
      notification: buildNotificationComponentStyles(theme),
      chartTypeIcon: buildChartTypeIconComponentStyles(theme),
      dataTable: buildDataTableComponentStyles(theme),
      dataTableWithGrouping: buildDataTableWithGroupingComponentStyles(theme),
      dashboardGrid: buildDashboardGridComponentStyles(theme),
      widgetOutputView: buildWidgetOutputViewComponentStyles(theme),
      dashboardCrossFilterBar: buildDashboardCrossFilterBarComponentStyles(theme),
      slideOverPanel: buildSlideOverPanelComponentStyles(theme),
      genericDialog: buildGenericDialogComponentStyles(theme),
      drillDownDialog: buildDrillDownDialogComponentStyles(theme),
      onboardingDialog: buildOnboardingDialogComponentStyles(theme),
      appFormField: buildAppFormFieldComponentStyles(theme),
      appRadioGroup: buildAppRadioGroupComponentStyles(theme),
      appToggleField: buildAppToggleFieldComponentStyles(theme),
      appColorField: buildAppColorFieldComponentStyles(theme),
      chartOptionPanels: buildChartOptionPanelsComponentStyles(theme),
      chartEditorPanel: buildChartEditorPanelComponentStyles(theme),
      interactiveTableView: buildInteractiveTableViewComponentStyles(theme),
      columnFormatSelect: buildColumnFormatSelectComponentStyles(theme),
      tableColumnsEditor: buildTableColumnsEditorComponentStyles(theme),
      dashboardDecorationView: buildDashboardDecorationViewComponentStyles(theme),
      decorationEditDialog: buildDecorationEditDialogComponentStyles(theme),
      decorationPalette: buildDecorationPaletteComponentStyles(theme),
      widgetEditor: buildWidgetEditorComponentStyles(theme),
      designSidebar: buildDesignSidebarComponentStyles(theme),
      widgetPalette: buildWidgetPaletteComponentStyles(theme),
      dashboardWidgetEditDialog: buildDashboardWidgetEditDialogComponentStyles(theme),
      openSearchDiscoverViewer: buildOpenSearchDiscoverViewerComponentStyles(theme),
      openSearchEventLogPanel: buildOpenSearchEventLogPanelComponentStyles(theme),
      openSearchIndexPicker: buildOpenSearchIndexPickerComponentStyles(theme),
      chatIndexPicker: buildChatIndexPickerComponentStyles(theme),
      jsonCodeEditor: buildJsonCodeEditorComponentStyles(theme),
      pdfViewer: buildPdfViewerComponentStyles(theme),
      dateRangeHeader: buildDateRangeHeaderComponentStyles(theme),
      queryEditor: buildQueryEditorComponentStyles(theme),
      assignedQueriesPanel: buildAssignedQueriesPanelComponentStyles(theme),
      decorationBinding: buildDecorationBindingComponentStyles(theme),
    },
    widgets: {
      sidenav: buildSidenavStyles(theme),
      navbar: buildNavbarStyles(theme),
      configurator: buildConfiguratorStyles(theme),
      breadcrumbs: buildBreadcrumbsStyles(theme),
      layoutContainers: buildLayoutContainerStyles(theme),
      legacy: buildLegacyWidgetStyles(theme),
    },
  };
}
